import * as Phaser from "phaser-ce"
import { log } from "../../common/Logging"
import { Movable } from "./Sprites"

export class ScreenConfig {

    fullScreen?: boolean
    width: number
    height: number
    widthOffset?: number
    heightOffset?: number
    scaleAmount: number
    screenWidth: number
    screenHeight: number

    constructor(fullScreen?: boolean) {
        this.fullScreen = fullScreen
        const defaultWidth = 800
        const docElement = document.documentElement

        const maxWidth = fullScreen ? undefined : 1200
        const maxHeight = fullScreen ? undefined : 900

        this.screenWidth = docElement.clientWidth
        this.screenHeight = docElement.clientHeight

        this.width = !fullScreen && this.screenWidth > maxWidth ? maxWidth : docElement.clientWidth
        this.height = !fullScreen && docElement.clientHeight > maxHeight ? maxHeight : docElement.clientHeight

        const ratio = this.height / this.width
        if (ratio > 0.75) {
            // Height is too tall
            this.height = Math.floor(this.width * 3 / 4)
        } else if (ratio < 0.75) {
            // Width is too wide
            this.width = Math.floor(this.height * 4 / 3)
        }
        this.scaleAmount = this.width / defaultWidth

        if (fullScreen) {
            this.widthOffset = (this.screenWidth - this.width) / 2
            this.heightOffset = (docElement.clientHeight - this.height) / 2
        } else {
            this.widthOffset = 0
            this.heightOffset = 0
        }
    }

    toString = (): string => {
        return "ScreenConfig:\nwidth: " + this.width + "\nheight:" + this.height + "\nscaleAmount:" + this.scaleAmount
    }
}

export class Scale {
    static cachedValues = new ScreenConfig()

    // Currently this only will resize the screen to be smaller. I'm having an issue with it not displaying all the
    // screen when resized to be bigger.
    static resizeScreen = (game?: Phaser.Game, fullScreen?: boolean) => {
        Scale.cachedValues = new ScreenConfig(fullScreen == null ? Scale.cachedValues.fullScreen : fullScreen)
        if (game && game.scale) {
            if (fullScreen) {
                const docElement = document.documentElement
                game.scale.setGameSize(docElement.clientWidth, docElement.clientHeight)
            } else {
                game.scale.setGameSize(Scale.cachedValues.width, Scale.cachedValues.height)
            }
        }
        log.debug("Resized the screen. New scale amount:\n" + Scale.cachedValues.toString())
    }

    /**
     * This gets the available width regardless of full screen vs partial screen.
     */
    static availableWidth = () => Scale.cachedValues.fullScreen ? Scale.cachedValues.screenWidth : Scale.width()

    /**
     * This is the width of the screen at its with scaling ratio. This is also absolute width when not in fullscreen mode.
     */
    static width = () => Scale.cachedValues.width

    static availableHeight = () => Scale.cachedValues.fullScreen ? Scale.cachedValues.screenHeight : Scale.height()

    /**
     * Height of the screen at its scaling ratio. This is also absolute height when not in fullscreen mode.
     */
    static height = () => Scale.cachedValues.height

    /**
     * This is the position on screen for fullscreen or non-fullscreen. At percent = 1 it will be equal to width() for non-fullscreen, and larger for
     * fullscreen.
     */
    static x = (percent = 1): number => Scale.width() * percent + Scale.cachedValues.widthOffset

    /**
     * This is the position on screen for fullscreen or non-fullscreen. At percent = 1 it will be equal to height() for non-fullscreen, and larger for
     * fullscreen.
     */
    static y = (percent = 1): number => Scale.height() * percent + Scale.cachedValues.heightOffset

    static scaleAmount = (): number => {
        return Scale.cachedValues.scaleAmount
    }

    static scaleSprite = (sprite: Phaser.Sprite | Phaser.Image, scale = 1) => sprite.scale.setTo(scale * Scale.cachedValues.scaleAmount)
    static scaleSpriteXY = (sprite: Phaser.Sprite | Phaser.Image, scaleX = 1, scaleY = 1) => {
        return sprite.scale.setTo(scaleX * Scale.cachedValues.scaleAmount, scaleY * Scale.cachedValues.scaleAmount)
    }

    static flipX = (sprite: Phaser.Sprite | Phaser.Image) => {
        sprite.scale.setTo(sprite.scale.x * -1, sprite.scale.y)
        sprite.anchor.setTo(1, 0)
    }

    static spritesCurrentScale = (sprite: Phaser.Sprite | Phaser.Image): number => {
        return sprite.scale.x / Scale.cachedValues.scaleAmount
    }

    static lockScaleSprite = (sprite: Phaser.Sprite, scale = 1) => {
        const realScale = scale * Scale.cachedValues.scaleAmount
        sprite.scale.setTo(scale * Scale.cachedValues.scaleAmount)
        sprite.setScaleMinMax(realScale, realScale)
    }

    static fillScreen = (sprite: Movable) => {
        Scale.fillScreenHeight(sprite)
        Scale.fillScreenWidth(sprite)
    }

    static fillScreenHeight = (sprite: Movable) => {
        const originalHeight = sprite.height
        sprite.height = Scale.height()
        const ratio = originalHeight / sprite.height
        sprite.width = Math.floor(sprite.width / ratio)

        const padding = (Scale.width() - sprite.width) / 2
        sprite.x = Math.floor(padding)
    }

    static fillScreenWidth = (sprite: Movable) => {
        const originalWidth = sprite.width
        sprite.width = Scale.width()
        const ratio = originalWidth / sprite.width
        sprite.height = Math.floor(sprite.height / ratio)

        const padding = (Scale.height() - sprite.height) / 2
        sprite.y = Math.floor(padding)
    }

    static fillPortionOfScreenWidth = (sprite: Movable, portion = 1) => {
        const originalWidth = sprite.width
        sprite.width = Scale.width() * portion
        const ratio = originalWidth / sprite.width
        sprite.height = Math.floor(sprite.height / ratio)
    }

    static scaleToFitTwoVerticallyAndFourHorizontally = (sprite: Movable) => {
        const heightScale = Scale.availableHeight() / 2
        const widthScale = Scale.availableWidth() / 4

        // If heightScale is larger, width is the limiting factor, so scale images to fit width
        // Otherwise, height is the limiting factor, so scale images to fit height
        if (heightScale > widthScale) {
            const originalWidth = sprite.width
            sprite.width = widthScale
            const ratio = originalWidth / sprite.width
            sprite.height = Math.floor(sprite.height / ratio)
        } else {
            const originalHeight = sprite.height
            sprite.height = heightScale
            const ratio = originalHeight / sprite.height
            sprite.width = Math.floor(sprite.width / ratio)
        }
    }

    static scaleToFitHorizontal = (sprite: Movable, numToFit: number) => {
        const widthScale = Scale.availableWidth() / numToFit

        const originalWidth = sprite.width
        sprite.width = widthScale
        const ratio = originalWidth / sprite.width
        sprite.height = Math.floor(sprite.height / ratio)
    }

}

export const x = Scale.x
export const y = Scale.y
